import {
  Box, Card, CardContent, Chip, Grid, Stack, Typography,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import GavelIcon from '@mui/icons-material/Gavel';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { api } from '@/api/client';
import { clinician } from '@/api/endpoints';
import { formatDate, formatDateTime } from '@/utils/format';
import type { ClinicianLicense, VerifiableHealthCredential } from '@/types/medical';

/**
 * Clinician credentials.
 *
 * Three sections:
 *   1. Trust passport summary — the score + breakdown + risk flags.
 *      The clinician sees the same factors the public sees.
 *   2. Licenses — every ClinicianLicense row, with expiry warnings.
 *   3. Verifiable credentials — VCs issued for identity, malpractice cover,
 *      board certifications.
 */

export function ClinicianCredentialsPage() {
  const profileQ = useApiQuery({
    queryKey: ['clinician', 'me'],
    queryFn:  clinician.me.get,
  });
  const licensesQ = useApiQuery({
    queryKey: ['clinician', 'licenses'],
    queryFn:  () => api.get<ClinicianLicense[]>('/api/medical/clinician/licenses/'),
  });
  const credsQ = useApiQuery({
    queryKey: ['clinician', 'credentials'],
    queryFn:  () => api.get<VerifiableHealthCredential[]>('/api/medical/clinician/credentials/'),
  });

  return (
    <>
      <PageHeader
        title="Credentials"
        subtitle="Your trust passport — the same view patients see when they look at you."
      />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DataState query={profileQ} loadingLabel="Loading passport…">
            {(me) => me.trust_passport ? (
              <PassportCard passport={me.trust_passport} />
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Trust passport will be computed shortly after you add a license.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </DataState>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}>
            Licenses
          </Typography>
          <DataState query={licensesQ} empty={{ title: 'No licenses yet' }}>
            {(items) => (
              <Stack spacing={1.5}>
                {items.map((l) => <LicenseCard key={l.id} license={l} />)}
              </Stack>
            )}
          </DataState>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}>
            Verifiable credentials
          </Typography>
          <DataState query={credsQ} empty={{ title: 'No credentials yet' }}>
            {(items) => (
              <Stack spacing={1.5}>
                {items.map((c) => <CredentialCard key={c.id} cred={c} />)}
              </Stack>
            )}
          </DataState>
        </Grid>
      </Grid>
    </>
  );
}

function PassportCard({
  passport,
}: { passport: NonNullable<Parameters<typeof Object.keys>[0] & {
  trust_score: string;
  active_license_count: number;
  active_jurisdiction_codes: string[];
  can_prescribe_jurisdiction_codes: string[];
  can_prescribe_controlled_jurisdiction_codes: string[];
  risk_flags: { code: string; detail?: string }[];
  public_summary: { breakdown?: { key: string; weight: string; contributes: boolean; value: string }[] };
  last_computed_at: string | null;
}> }) {
  const score = parseFloat(passport.trust_score);
  const scoreColor = score >= 80 ? 'success.main'
                   : score >= 50 ? 'warning.main'
                                 : 'error.main';

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box sx={{ minWidth: 160, textAlign: 'center' }}>
            <Box sx={{
              width: 120, height: 120, mx: 'auto',
              borderRadius: '50%', border: 4, borderColor: scoreColor,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography variant="h2" component="div" sx={{ color: scoreColor, lineHeight: 1 }}
                          className="tabular">
                {Math.round(score)}
              </Typography>
              <Typography variant="caption" color="text.secondary">trust score</Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
              <Chip size="small" variant="outlined"
                    label={`${passport.active_license_count} licenses`} />
              <Chip size="small" variant="outlined"
                    label={`Jurisdictions: ${passport.active_jurisdiction_codes.join(', ') || '—'}`} />
              {passport.can_prescribe_jurisdiction_codes.length > 0 && (
                <Chip size="small" color="primary" variant="outlined"
                      label={`Can prescribe: ${passport.can_prescribe_jurisdiction_codes.join(', ')}`} />
              )}
            </Stack>
            {(passport.risk_flags ?? []).length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}>
                  Risk flags
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {passport.risk_flags.map((f, i) => (
                    <Chip key={i} size="small" color="warning" variant="outlined"
                          label={f.code.replace(/_/g, ' ')} />
                  ))}
                </Stack>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}>
                Score breakdown (each factor with its weight)
              </Typography>
              <Stack spacing={0.5}>
                {(passport.public_summary?.breakdown ?? []).map((b, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between"
                         alignItems="center">
                    <Typography variant="caption">
                      {b.key.replace(/_/g, ' ')}
                      {b.value && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}— {b.value}
                        </Typography>
                      )}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" className="tabular"
                                  color={b.contributes ? 'success.main' : 'text.disabled'}>
                        {b.contributes ? '+' : '–'}{b.weight}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mt: 1.5 }}>
              Last recomputed: {passport.last_computed_at ? formatDateTime(passport.last_computed_at) : '—'}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function LicenseCard({ license }: { license: ClinicianLicense }) {
  const expiringSoon = license.valid_to &&
    (new Date(license.valid_to).getTime() - Date.now()) < 60 * 24 * 3600 * 1000;
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <GavelIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
              <Typography variant="subtitle1" fontWeight={600} className="tabular">
                {license.license_number}
              </Typography>
              <Chip size="small" label={license.status}
                    color={license.status === 'active' ? 'success' : 'default'}
                    variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {license.jurisdiction_code} · {license.license_type.replace(/_/g, ' ')}
              {license.can_prescribe && ' · Rx'}
              {license.can_prescribe_controlled && ' · controlled'}
            </Typography>
            {license.valid_to && (
              <Typography
                variant="caption"
                color={expiringSoon ? 'warning.main' : 'text.secondary'}
                sx={{ display: 'block' }}
                className="tabular"
              >
                Valid until {formatDate(license.valid_to)}
                {expiringSoon && ' — expiring within 60 days'}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function CredentialCard({ cred }: { cred: VerifiableHealthCredential }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle',
            color: cred.is_active_now ? 'success.main' : 'text.disabled',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {cred.credential_type === 'malpractice_cover'
              ? <HealthAndSafetyIcon />
              : <VerifiedIcon />}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                {cred.credential_type.replace(/_/g, ' ')}
              </Typography>
              <Chip
                size="small" label={cred.status}
                color={cred.is_active_now ? 'success' : 'default'}
                variant="outlined" sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              issued by {cred.issuer_name}
              {cred.valid_to && ` · expires ${formatDate(cred.valid_to)}`}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
